import React, { useEffect, useState, useCallback } from "react";
import {
    Button,
    Card,
    CardBody,
    CardHeader,
    Col,
    Container,
    Input,
    ListGroup,
    ListGroupItem,
    Row,
    Spinner,
} from "reactstrap";
import { useParams, useNavigate } from "react-router-dom";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import {
    getAllPermissions,
    getRolePermissions,
    assignPermissionsToRole,
    getRole,
} from "../../../services/ValitanApiService";
import { enqueueSnackbar } from "notistack";

const AssignPermissionsPage = () => {
    document.title = "Assign Permissions | Valitan";

    const { roleId } = useParams();
    const navigate = useNavigate();

    const [roleName, setRoleName] = useState("");
    const [allPermissions, setAllPermissions] = useState([]);
    const [assignedPermissions, setAssignedPermissions] = useState({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [searchAvailable, setSearchAvailable] = useState("");
    const [searchAssigned, setSearchAssigned] = useState("");

    // pagination
    const [paginationAvailable, setPaginationAvailable] = useState({
        pageNumber: 0,
        pageSize: 10,
    });
    const [paginationAssigned, setPaginationAssigned] = useState({
        pageNumber: 0,
        pageSize: 10,
    });

    const loadData = useCallback(async () => {
        try {
            setLoading(true);

            // Role details
            const roleResp = await getRole(roleId);
            if (roleResp.status === 200) {
                setRoleName(roleResp.data.name || "Unknown Role");
            }

            // All permissions
            let page = 0;
            const size = 100;
            let fetched = [];
            let hasMore = true;
            while (hasMore) {
                const resp = await getAllPermissions(page, size);
                const content = resp?.data?.data?.content || [];
                fetched = [...fetched, ...content];
                hasMore = content.length === size;
                page++;
            }
            setAllPermissions(fetched);

            // Assigned permissions
            const rolePermResp = await getRolePermissions(roleId, 0, 1000);
            const rolePerms = rolePermResp?.data?.data?.content || [];

            const assigned = {};
            rolePerms.forEach((rp) => {
                const permId = String(rp.permission?.id);
                if (permId) {
                    assigned[permId] = rp.scope || "ALL";
                }
            });
            setAssignedPermissions(assigned);
        } catch (err) {
            alert("Failed to load permissions or role data.");
            console.error("Error loading data:", err);
        } finally {
            setLoading(false);
        }
    }, [roleId]);

    useEffect(() => {
        if (roleId) loadData();
        else {
            alert("Invalid role ID.");
            navigate("/roles");
        }
    }, [roleId, loadData, navigate]);

    const moveToAssigned = (permId) => {
        setAssignedPermissions((prev) => ({
            ...prev,
            [permId]: "ALL",
        }));
    };

    const moveToAvailable = (permId) => {
        setAssignedPermissions((prev) => {
            const updated = { ...prev };
            delete updated[permId];
            return updated;
        });
    };

    const assignAll = () => {
        const updated = { ...assignedPermissions };
        filteredAvailable.forEach((p) => {
            updated[String(p.id)] = "ALL";
        });
        setAssignedPermissions(updated);
    };

    const unAssignAll = () => {
        setAssignedPermissions({});
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const permissionsPayload = Object.entries(assignedPermissions).map(
                ([permissionId, scope]) => ({
                    permission_id: permissionId,
                    scope: scope || "ALL",
                })
            );

            console.log("📤 Save Payload:", {
                role_id: roleId,
                permissions: permissionsPayload,
            });

            const resp = await assignPermissionsToRole(roleId, permissionsPayload);

            if (resp.status === 200 || resp.status === 201) {
                const serverMessage =
                    resp.data?.message || "Permissions updated successfully!";

                enqueueSnackbar(`${serverMessage}`, {
                    variant: "success",
                    autoHideDuration: 4000,
                });
            } else {
                throw new Error("Unexpected response status: " + resp.status);
            }
        } catch (err) {
            enqueueSnackbar("❌ Error saving permissions.", {
                variant: "error",
                autoHideDuration: 4000,
            });
            console.error("Error saving permissions:", err);
        } finally {
            setSaving(false);
        }
    };


    // --- Filtering ---
    const assignedIds = Object.keys(assignedPermissions);

    const filteredAvailable = allPermissions.filter(
        (p) =>
            !assignedIds.includes(String(p.id)) &&
            (p.controller?.toLowerCase().includes(searchAvailable.toLowerCase()) ||
                p.end_point?.toLowerCase().includes(searchAvailable.toLowerCase()) ||
                p.method?.toLowerCase().includes(searchAvailable.toLowerCase()))
    );

    const filteredAssigned = allPermissions.filter(
        (p) =>
            assignedIds.includes(String(p.id)) &&
            (p.controller?.toLowerCase().includes(searchAssigned.toLowerCase()) ||
                p.end_point?.toLowerCase().includes(searchAssigned.toLowerCase()) ||
                p.method?.toLowerCase().includes(searchAssigned.toLowerCase()))
    );

    // --- Pagination ---
    const startAvailable = paginationAvailable.pageNumber * paginationAvailable.pageSize;
    const endAvailable = startAvailable + paginationAvailable.pageSize;
    const currentAvailable = filteredAvailable.slice(startAvailable, endAvailable);
    const totalPagesAvailable = Math.ceil(filteredAvailable.length / paginationAvailable.pageSize);

    const startAssigned = paginationAssigned.pageNumber * paginationAssigned.pageSize;
    const endAssigned = startAssigned + paginationAssigned.pageSize;
    const currentAssigned = filteredAssigned.slice(startAssigned, endAssigned);
    const totalPagesAssigned = Math.ceil(filteredAssigned.length / paginationAssigned.pageSize);

    return (
        <div className="page-content">
            <Container fluid>
                <Breadcrumbs
                    title="Roles Management"
                    breadcrumbItem={`Assign Permissions: ${roleName}`}
                />
                <Row>
                    {loading ? (
                        <div className="text-center p-5">
                            <Spinner color="primary" />
                        </div>
                    ) : (
                        <>
                            {/* LEFT: Available Permissions */}
                            <Col md={5}>
                                <Card>
                                    <CardHeader className="d-flex justify-content-between align-items-center">
                                        <h5 className="mb-0">Available Permissions</h5>
                                        <div className="d-flex gap-2">
                                            <Input
                                                type="text"
                                                placeholder="Search..."
                                                value={searchAvailable}
                                                onChange={(e) => {
                                                    setSearchAvailable(e.target.value);
                                                    setPaginationAvailable((prev) => ({
                                                        ...prev,
                                                        pageNumber: 0,
                                                    }));
                                                }}
                                                style={{ width: "200px" }}
                                            />
                                            <Button size="sm" color="success" onClick={assignAll}>
                                                Assign All
                                            </Button>
                                        </div>
                                    </CardHeader>
                                    <CardBody style={{ maxHeight: "60vh", overflowY: "auto" }}>
                                        <ListGroup>
                                            {currentAvailable.map((perm) => (
                                                <ListGroupItem
                                                    key={perm.id}
                                                    className="d-flex justify-content-between align-items-center"
                                                >
                                                    <div>
                                                        <strong>{perm.controller}</strong>{" "}
                                                        <small>
                                                            ({perm.method}) {perm.end_point}
                                                        </small>
                                                    </div>
                                                    <Button
                                                        size="sm"
                                                        color="primary"
                                                        onClick={() => moveToAssigned(String(perm.id))}
                                                    >
                                                        ➡
                                                    </Button>
                                                </ListGroupItem>
                                            ))}
                                            {currentAvailable.length === 0 && (
                                                <div className="text-muted text-center">
                                                    No matching permissions
                                                </div>
                                            )}
                                        </ListGroup>
                                    </CardBody>
                                    {/* Pagination controls */}
                                    <div className="d-flex justify-content-between align-items-center p-2">
                                        <Button
                                            color={
                                                paginationAvailable.pageNumber === 0
                                                    ? "secondary"
                                                    : "primary"
                                            }
                                            disabled={paginationAvailable.pageNumber === 0}
                                            onClick={() =>
                                                setPaginationAvailable((p) => ({
                                                    ...p,
                                                    pageNumber: p.pageNumber - 1,
                                                }))
                                            }
                                        >
                                            Previous
                                        </Button>
                                        <span>
                                            Page {paginationAvailable.pageNumber + 1} of{" "}
                                            {totalPagesAvailable || 1}
                                        </span>
                                        <Button
                                            color={
                                                paginationAvailable.pageNumber >= totalPagesAvailable - 1
                                                    ? "secondary"
                                                    : "primary"
                                            }
                                            disabled={
                                                paginationAvailable.pageNumber >= totalPagesAvailable - 1
                                            }
                                            onClick={() =>
                                                setPaginationAvailable((p) => ({
                                                    ...p,
                                                    pageNumber: p.pageNumber + 1,
                                                }))
                                            }
                                        >
                                            Next
                                        </Button>
                                    </div>
                                </Card>
                            </Col>

                            {/* RIGHT: Assigned Permissions */}
                            <Col md={7}>
                                <Card>
                                    <CardHeader className="d-flex justify-content-between align-items-center">
                                        <h5 className="mb-0">
                                            Assigned Permissions for{" "}
                                            <span className="text-primary">{roleName}</span>
                                        </h5>
                                        <div className="d-flex gap-2">
                                            <Input
                                                type="text"
                                                placeholder="Search..."
                                                value={searchAssigned}
                                                onChange={(e) => {
                                                    setSearchAssigned(e.target.value);
                                                    setPaginationAssigned((prev) => ({
                                                        ...prev,
                                                        pageNumber: 0,
                                                    }));
                                                }}
                                                style={{ width: "200px" }}
                                            />
                                            <Button size="sm" color="danger" onClick={unAssignAll}>
                                                Unassign All
                                            </Button>
                                        </div>
                                    </CardHeader>
                                    <CardBody style={{ maxHeight: "60vh", overflowY: "auto" }}>
                                        <ListGroup>
                                            {currentAssigned.map((perm) => (
                                                <ListGroupItem
                                                    key={perm.id}
                                                    className="d-flex justify-content-between align-items-center"
                                                >
                                                    <div>
                                                        <strong>{perm.controller}</strong>{" "}
                                                        <small>
                                                            ({perm.method}) {perm.end_point}
                                                        </small>
                                                    </div>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <Input
                                                            type="select"
                                                            size="sm"
                                                            value={assignedPermissions[String(perm.id)]}
                                                            onChange={(e) =>
                                                                setAssignedPermissions((prev) => ({
                                                                    ...prev,
                                                                    [perm.id]: e.target.value,
                                                                }))
                                                            }
                                                        >
                                                            <option value="ALL">ALL</option>
                                                            <option value="OWN">OWN</option>
                                                        </Input>
                                                        <Button
                                                            size="sm"
                                                            color="danger"
                                                            onClick={() => moveToAvailable(String(perm.id))}
                                                        >
                                                            ⬅
                                                        </Button>
                                                    </div>
                                                </ListGroupItem>
                                            ))}
                                            {currentAssigned.length === 0 && (
                                                <div className="text-muted text-center">
                                                    No permissions assigned
                                                </div>
                                            )}
                                        </ListGroup>
                                    </CardBody>
                                    {/* Pagination controls */}
                                    <div className="d-flex justify-content-between align-items-center p-2">
                                        <Button
                                            color={
                                                paginationAssigned.pageNumber === 0
                                                    ? "secondary"
                                                    : "primary"
                                            }
                                            disabled={paginationAssigned.pageNumber === 0}
                                            onClick={() =>
                                                setPaginationAssigned((p) => ({
                                                    ...p,
                                                    pageNumber: p.pageNumber - 1,
                                                }))
                                            }
                                        >
                                            Previous
                                        </Button>
                                        <span>
                                            Page {paginationAssigned.pageNumber + 1} of{" "}
                                            {totalPagesAssigned || 1}
                                        </span>
                                        <Button
                                            color={
                                                paginationAssigned.pageNumber >= totalPagesAssigned - 1
                                                    ? "secondary"
                                                    : "primary"
                                            }
                                            disabled={
                                                paginationAssigned.pageNumber >= totalPagesAssigned - 1
                                            }
                                            onClick={() =>
                                                setPaginationAssigned((p) => ({
                                                    ...p,
                                                    pageNumber: p.pageNumber + 1,
                                                }))
                                            }
                                        >
                                            Next
                                        </Button>
                                    </div>
                                </Card>
                            </Col>
                        </>
                    )}
                </Row>

                {/* Bottom Actions */}
                <div className="d-flex justify-content-end mt-3">
                    <Button
                        color="secondary"
                        className="me-2"
                        onClick={() => navigate("/roles")}
                        disabled={saving}
                    >
                        Cancel
                    </Button>
                    <Button color="primary" onClick={handleSave} disabled={saving}>
                        {saving ? <Spinner size="sm" /> : "Save Changes"}
                    </Button>
                </div>
            </Container>
        </div>
    );
};

export default AssignPermissionsPage;
